import React from 'react';
import { Link } from 'react-router-dom';
import TopNav from './TopNav';
import SolutionsFooter from './SolutionsFooter';
import HowToEngage from './HowToEngage';
import useSEO from '../hooks/useSEO';

function MiningManufacturingExample({ logo }) {
  useSEO({
    title: 'Mining & Manufacturing Simulation',
    description: 'Simulate equipment lifecycles and maintenance strategies. Prevent costly unplanned downtime before it strikes.'
  });

  return (
    <div className="min-h-screen bg-white">
      <TopNav />

      <main className="max-w-5xl mx-auto px-8 py-16 md:py-24">
        <div className="text-center mb-16">
          <p className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Asset Uptime + Maintenance</p>
          <h1 className="text-4xl md:text-5xl font-medium text-gray-900 mb-6 leading-tight tracking-tight">Prevent costly unplanned downtime before it strikes</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">Simulate equipment lifecycles and maintenance strategies to optimize asset availability—before breakdowns halt production.</p>
        </div>

        <div className="mb-16">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6 text-center">Who this is for</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center"><p className="font-medium text-gray-900 mb-2">Operations Directors</p><p className="text-sm text-gray-600">Maximizing production while managing costs</p></div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center"><p className="font-medium text-gray-900 mb-2">Maintenance Managers</p><p className="text-sm text-gray-600">Balancing preventive vs. reactive strategies</p></div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center"><p className="font-medium text-gray-900 mb-2">Asset Planners</p><p className="text-sm text-gray-600">Optimizing fleet and equipment investments</p></div>
          </div>
        </div>

        <div className="mb-16">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6 text-center">Decisions this improves</h2>
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">When to schedule preventive maintenance vs. run to failure</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">How many spare parts to stock at each location</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">When to replace equipment vs. extend its life</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">How to sequence shutdowns to minimize impact</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">Trade-offs between production targets and asset health</p></div>
              <div className="flex items-start"><span className="text-gray-400 mr-3 mt-1">—</span><p className="text-gray-700">How weather and operating conditions affect reliability</p></div>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <div className="bg-gray-900 text-white rounded-lg p-8 md:p-10">
            <p className="text-sm font-medium text-gray-400 uppercase tracking-wider mb-4">Example: Mining Operations</p>
            <p className="text-lg leading-relaxed mb-6">A mining operation simulated haul truck fleet maintenance schedules, spare parts inventory levels, and weather-related delays. The model revealed that shifting to condition-based maintenance for critical components—combined with strategic parts positioning—would reduce unplanned downtime by 35% and extend average equipment lifespan by two years.</p>
            <p className="text-gray-400 text-sm">The impact: $8M annual savings from improved uptime and reduced emergency repairs.</p>
          </div>
        </div>

                <HowToEngage
          platformDescription="Model your asset fleet, test maintenance strategies, and share insights with operations. Ideal for reliability teams who want ongoing decision support."
          consultingDescription="Partner with our industrial consultants to build a custom asset model, run optimization workshops, and deliver implementation-ready strategies."
        />

        <div className="pt-8 border-t border-gray-200">
          <h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-6">Related solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/solutions/supply-chain" className="text-gray-700 hover:text-gray-900 text-sm">Inventory + Production →</Link>
            <Link to="/solutions/energy-utilities" className="text-gray-700 hover:text-gray-900 text-sm">Grid + Demand Management →</Link>
            <Link to="/solutions/logistics-transportation" className="text-gray-700 hover:text-gray-900 text-sm">Route + Fleet Optimization →</Link>
          </div>
        </div>
      </main>

      <SolutionsFooter />
    </div>
  );
}

export default MiningManufacturingExample;
